import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

// Feature-scoped API module for receipt feedback — the 1-5 star ratings customers leave by
// scanning the QR on a POS receipt. Uses the shared axios instance (Bearer auth + 401).
//
// WHY THIS SCREEN EXISTS, which shapes the API surface: the feedback is only useful once it
// reaches Odoo, and Odoo will only create a review when that order's uid has already synced
// there. An early push therefore does nothing, retries on a ladder (15m → 2h → 8h → 24h),
// and after 5 attempts gives up as `stale`. Finding and re-driving the stale pile is the
// job; browsing is secondary. Hence the status filter and the bulk re-sync.
//
// Convention, matching brandApi.js: reads return data or a safe default and toast on error;
// mutations return the record or null and let the caller toast success.

const read = async (url, params, fallback, label) => {
  try {
    const res = await axiosInstance.get(url, { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? `Error loading ${label}`);
    return fallback;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Error loading ${label}`);
    return fallback;
  }
};

const EMPTY_PAGE = { rows: [], total: 0, page: 1, pageSize: 25, totalPages: 1 };

/**
 * Server-side paginated and filtered. Unlike the Brands screen this does NOT fetch
 * everything and filter in the browser: a busy merchant accumulates a feedback row per
 * receipt, so the table has to stay paged at the source.
 *
 * @param {object} filters { merchantId, branchId, rating, pushStatus, from, to, page, pageSize }
 */
export const getCustomerFeedback = (filters = {}) => {
  // Blank values must not travel — the server validates `rating` as an integer and
  // `pushStatus` against an enum, so an empty string is a 400, not "no filter".
  const params = Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) acc[key] = value;
    return acc;
  }, {});

  return read("/api/merchant/customerFeedback", params, EMPTY_PAGE, "feedback");
};

/**
 * Re-arm rows for the next push. Takes an array because the realistic action is "the orders
 * finally landed in Odoo, re-drive everything that went stale", not one row at a time.
 *
 * Returns { updated } — already-synced rows are skipped server-side, so `updated` can be
 * lower than the number of ids sent, and that is not an error.
 */
export const retryCustomerFeedbackPush = async (ids, merchantId) => {
  try {
    const res = await axiosInstance.post("/api/merchant/customerFeedback/retryPush", {
      ids,
      ...(merchantId && merchantId !== "all" ? { merchantId } : {}),
    });
    if (res?.data && !res.data.isError) {
      return { updated: res.data.data?.updated ?? 0, message: res.data.message };
    }
    errorToast(res?.data?.message ?? "Error queuing re-sync");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error queuing re-sync");
    return null;
  }
};
